'use client';

import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { forwardRef } from 'react';

import { type ButtonProps, Button } from '../Button';
import { ScrollArea } from '../ScrollArea';
import { cn } from '../utils/cn';

const DialogPortal = DialogPrimitive.Portal;
const DialogClose = DialogPrimitive.Close;

const DialogOverlay = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-[100000] bg-slate-600/50 backdrop-blur-sm',
      className,
    )}
    {...props}
  />
));

/**
 * A window overlaid on either the primary window or another dialog window, rendering the content underneath inert.
 * @see {@link https://stylus.design/dialog}
 */
export const Dialog = DialogPrimitive.Root;

/**
 * A button that opens a `Dialog`.
 * @see {@link https://stylus.design/dialog}
 */
export const DialogTrigger = DialogPrimitive.Trigger;

export interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  description?: string | React.ReactNode;
  hideClose?: boolean;
  img?: {
    src: string;
    alt: string;
  };
}

/**
 * A header for a `Dialog`, containing a title, optional description, and close button.
 * @see {@link https://stylus.design/dialog}
 */
export const DialogHeader = ({
  title,
  description,
  hideClose,
  className,
  img,
  children,
  ...props
}: DialogHeaderProps) => (
  <header className={cn(!img && 'sticky top-0 z-10', className)} {...props}>
    {img && (
      <img
        height={200}
        className='animate-bg-pulse block w-full bg-slate-300 object-cover'
        src={img.src}
        alt={img.alt}
      />
    )}
    <div
      className={cn(
        'flex items-center gap-3 rounded-t-xl bg-gradient-to-b from-white via-white/95 via-85% to-transparent p-6',
      )}
    >
      <div className='flex flex-1 flex-col gap-1'>
        <DialogPrimitive.Title
          className={cn(
            'm-0 -mt-0.5 font-sans text-xl font-semibold leading-7 tracking-tight text-slate-900',
            className,
          )}
        >
          {title}
        </DialogPrimitive.Title>
        {description && (
          <DialogPrimitive.Description className='m-0 text-sm font-normal text-slate-600'>
            {description}
          </DialogPrimitive.Description>
        )}
      </div>
      {children && <div className='ml-auto'>{children}</div>}
      {!hideClose && (
        <DialogPrimitive.Close data-testid='closeButton' asChild>
          <Button
            variant='ghost'
            icon={faXmark}
            aria-label='Close'
            className={cn(
              'size-7 shrink-0 self-start justify-self-end bg-transparent',
              !img && '-mr-3 -mt-3',
              !!img &&
                'absolute right-4 top-4 bg-black/50 text-white hover:bg-black/80 hover:opacity-100',
            )}
          />
        </DialogPrimitive.Close>
      )}
    </div>
  </header>
);

export interface DialogMainProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/**
 * The main content of a `Dialog`.
 * @see {@link https://stylus.design/dialog}
 */
export const DialogMain = ({ children, className, ...props }: DialogMainProps) => (
  <div className={cn('px-6', className)} {...props}>
    {children}
  </div>
);

export interface DialogFooterProps {
  primaryAction?: ButtonProps;
  secondaryAction?: ButtonProps;
  primaryActionClose?: boolean;
  secondaryActionClose?: boolean;
  className?: string;
  children?: React.ReactNode;
}

/**
 * A footer for a `Dialog` that can display one or more buttons.
 * @see {@link https://stylus.design/dialog}
 */
export const DialogFooter = ({
  className,
  children,
  primaryAction,
  secondaryAction,
  primaryActionClose = true,
  secondaryActionClose = true,
  ...props
}: DialogFooterProps) => (
  <footer
    className={cn(
      '@[380px]:justify-end @[380px]:flex-row @[380px]:items-center sticky bottom-0 flex flex-col justify-stretch gap-3 rounded-b-xl bg-gradient-to-t from-white via-white/95 via-85% to-transparent p-6',
      className,
    )}
    {...props}
  >
    {children && <div className='flex-1'>{children}</div>}
    <div className='@[380px]:flex-row flex flex-col-reverse gap-3'>
      {secondaryAction && (
        <>
          {secondaryActionClose ? (
            <DialogClose asChild>
              <Button variant='secondary' {...secondaryAction} />
            </DialogClose>
          ) : (
            <Button variant='secondary' {...secondaryAction} />
          )}
        </>
      )}
      {primaryAction && (
        <DialogClose asChild>
          <Button {...primaryAction} />
        </DialogClose>
      )}
    </div>
  </footer>
);

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /**
   * Whether the dialog should be scrollable.
   * @default true
   */
  scrollable?: boolean;
  /**
   * The container where the dialog content will be rendered.
   * @default document.body
   */
  container?: HTMLElement | null;
}

/**
 * Contains content to be rendered in the open dialog.
 * @see {@link https://stylus.design/dialog}
 */
export const DialogContent = forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, scrollable = true, container, ...props }, ref) => (
  <DialogPortal container={container}>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        '@container data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] fixed left-[50%] top-[50%] z-[100000] max-h-[calc(100dvh_-_5rem)] w-full max-w-[min(calc(100vw_-_2.5rem),_440px)] -translate-x-1/2 -translate-y-1/2 rounded-xl bg-white shadow-xl duration-200 first:rounded-t-xl last:rounded-b-xl [&_header_+_footer]:pt-0',
        className,
      )}
      {...props}
    >
      {scrollable ? (
        <ScrollArea
          className='first:rounded-t-xl last:rounded-b-xl [&>[data-radix-scroll-area-viewport]]:max-h-[calc(100dvh_-_5rem)] [&_[data-orientation=vertical]]:py-2'
          type='auto'
        >
          {children}
        </ScrollArea>
      ) : (
        children
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
));
